import { DateTime } from 'luxon';
import InsightsDateRange from '../models/InsightsDateRange';
import { BaseInsightsQueryParams } from '../../Router/constants';

const INSIGHTS_DATE_RANGE_LOCAL_STORAGE_KEY = 'insightsDateRange';

class InvalidDateRangeException extends Error {}

const getDefaultDateRange = (): InsightsDateRange => {
  const previousMonthDate = DateTime.now().minus({ months: 1 });
  const previousMonthStartDate = previousMonthDate.startOf('month').toISODate();
  const previousMonthEndDate = previousMonthDate.endOf('month').toISODate();

  return { startDate: previousMonthStartDate, endDate: previousMonthEndDate };
};

const getInsightsDateRangeFromQueryParams = (queryParams: URLSearchParams): InsightsDateRange | undefined => {
  const startDateParam = queryParams.get(BaseInsightsQueryParams.START_DATE);
  const endDateParam = queryParams.get(BaseInsightsQueryParams.END_DATE);

  if (!startDateParam && !endDateParam) {
    return;
  }

  if (Boolean(startDateParam) !== Boolean(endDateParam)) {
    throw new InvalidDateRangeException();
  }

  const startDate = DateTime.fromISO(<string>startDateParam);
  const endDate = DateTime.fromISO(<string>endDateParam);

  if (!startDate.isValid || !endDate.isValid) {
    throw new InvalidDateRangeException();
  }

  if (startDate > endDate) {
    throw new InvalidDateRangeException();
  }

  return {
    startDate: startDate.toISODate(),
    endDate: endDate.toISODate(),
  };
};

const updateInsightsDateRangeInLocalStorage = (dateRange: InsightsDateRange) => {
  localStorage.setItem(INSIGHTS_DATE_RANGE_LOCAL_STORAGE_KEY, JSON.stringify(dateRange));
};

const getInsightsDateRangeInLocalStorage = () => {
  return localStorage.getItem(INSIGHTS_DATE_RANGE_LOCAL_STORAGE_KEY);
};

const getInsightsDateRange = (queryParams: URLSearchParams): InsightsDateRange => {
  try {
    const dateRangeFromUrl = getInsightsDateRangeFromQueryParams(queryParams);
  
    if (dateRangeFromUrl) {
      updateInsightsDateRangeInLocalStorage(dateRangeFromUrl);
      return dateRangeFromUrl;
    }
  
    const dateRangeFromLocalStorage = getInsightsDateRangeInLocalStorage();
    if (dateRangeFromLocalStorage) {
      return JSON.parse(dateRangeFromLocalStorage) as InsightsDateRange;
    }

    return getDefaultDateRange();
  } catch {
    localStorage.removeItem(INSIGHTS_DATE_RANGE_LOCAL_STORAGE_KEY);
    return getDefaultDateRange();
  }
};

export { getInsightsDateRange, updateInsightsDateRangeInLocalStorage };
